/**
 * AI対話式のシグネチャ商品プロファイル作成画面。
 * 詳細設計: docs/design/v02-signature-product-profile-design.md §5-3
 *
 * 店舗担当者が「乳化度」等を正確に言語化できない前提のため、AIが比較質問中心に対話しながら
 * ベーススープ等の構成要素を聞き取り、tool_use でサーバ側の下書きプロファイルへ直接反映する。
 * 右パネルは下書きの登録内容を随時再取得して表示する (対話と構造化編集が同じデータを指すため)。
 */
import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles, Send, Loader2, ArrowLeft, CheckCircle2, Square } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { postSSE } from '@/lib/sse';
import { toast } from '@/stores/toast';
import { isNearBottom, isAwaitingGivenUp } from '@/lib/chat-scroll';
import { ResponseMissingNotice } from '@/components/ai-menu/ResponseMissingNotice';

type ContentBlock = { type: string; text?: string };
type ChatMessage = { message_id: string; role: 'user' | 'assistant' | 'tool_use' | 'tool_result'; content: ContentBlock[]; created_at: string };
type SessionDetail = { session_id: string; title: string | null; status: string; session_type: string; draft_profile_id: string | null; messages: ChatMessage[] };
type ProfileDetail = {
  profile_name: string;
  status: 'draft' | 'active';
  components: Array<{ component_id: string; component_type: string; component_name: string }>;
};

const COMPONENT_TYPE_LABELS: Record<string, string> = {
  base_soup: 'ベーススープ', tare: 'かえし', aroma_oil: '香味油', noodle: '麺', topping_default: 'トッピング', other: 'その他',
};

const textOf = (content: ContentBlock[]): string =>
  content.filter((b) => b.type === 'text' && b.text).map((b) => b.text).join('\n');

export function ProfileBuilderChatPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [input, setInput] = useState('');
  const [streamingText, setStreamingText] = useState('');
  const [sending, setSending] = useState(false);
  const [finalized, setFinalized] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  // AiMenuChatPage.tsx と同じ修正 (2026-08-02、2回目): 距離を都度計算する方式だと、読み返すために
  // 一度上へスクロールした後は本文が伸び続ける限り「最下部付近」の条件に戻れず自動追従が復帰しない
  // 不具合があったため、scrollイベントで能動的に張り付き状態を更新する方式に変更する
  const stickToBottomRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);

  const { data: sessionDetail, refetch: refetchDetail } = useQuery<SessionDetail>({
    queryKey: ['ai-menu-session', sessionId],
    queryFn: () => apiFetch<SessionDetail>(`/menu-chat/sessions/${sessionId}`),
    enabled: Boolean(sessionId),
    // AiMenuChatPage.tsx と同じ修正 (2026-08-03、ユーザー報告): 生成中に画面遷移すると
    // sending/streamingText が破棄され「止まった」ように見えるが、生成自体(ツール呼出ループ含む)
    // はサーバ側で継続する。最後のメッセージがuserのままの間はポーリングし自動追従させる
    refetchInterval: (query) => {
      if (sending) return false;
      const messages = query.state.data?.messages;
      if (!messages?.length) return false;
      if (isAwaitingGivenUp(messages.at(-1), Date.now())) return false;
      return messages.at(-1)?.role === 'user' ? 4000 : false;
    },
    refetchIntervalInBackground: true,
  });

  const draftProfileId = sessionDetail?.draft_profile_id ?? null;
  const { data: draftProfile, refetch: refetchProfile } = useQuery<ProfileDetail>({
    queryKey: ['signature-profile', draftProfileId],
    queryFn: () => apiFetch<ProfileDetail>(`/menu-dev/profiles/${draftProfileId}`),
    enabled: Boolean(draftProfileId),
  });

  // AiMenuChatPage.tsx と同じ修正 (2026-08-02): scrollIntoViewが祖先の全スクロールコンテナを
  // 巻き込んでヘッダーごと画面外に押し出す不具合が本番で発生したため、内側コンテナを直接操作する
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const onScroll = () => {
      stickToBottomRef.current = isNearBottom(container.scrollHeight, container.scrollTop, container.clientHeight);
    };
    container.addEventListener('scroll', onScroll);
    return () => container.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (stickToBottomRef.current) container.scrollTop = container.scrollHeight;
  }, [sessionDetail?.messages.length, streamingText]);

  useEffect(() => {
    if (draftProfile?.status === 'active') setFinalized(true);
  }, [draftProfile?.status]);

  const send = async (content: string) => {
    if (!content.trim() || !sessionId) return;
    stickToBottomRef.current = true;
    setInput('');
    setSending(true);
    setStreamingText('');
    setJustStopped(false);
    const controller = new AbortController();
    abortRef.current = controller;

    qc.setQueryData<SessionDetail | undefined>(['ai-menu-session', sessionId], (prev) => prev && ({
      ...prev,
      messages: [...prev.messages, { message_id: `optimistic-${Date.now()}`, role: 'user', content: [{ type: 'text', text: content }], created_at: new Date().toISOString() }],
    }));

    try {
      await postSSE(`/menu-chat/sessions/${sessionId}/messages`, { content }, {
        onEvent: (event, data) => {
          if (event === 'delta') {
            const parsed = JSON.parse(data) as { text: string };
            setStreamingText((prev) => prev + parsed.text);
          } else if (event === 'done') {
            const parsed = JSON.parse(data) as { message_id: string; finalized?: boolean };
            setStreamingText('');
            refetchDetail();
            refetchProfile();
            if (parsed.finalized) {
              setFinalized(true);
              toast.success('プロファイルの登録が完了しました');
            }
          } else if (event === 'error') {
            const parsed = JSON.parse(data) as { message: string };
            toast.error(parsed.message);
            setStreamingText('');
            refetchDetail();
          }
        },
      }, controller.signal);
    } catch (e) {
      if (!controller.signal.aborted) {
        toast.error(e instanceof Error ? e.message : 'AI応答の取得に失敗しました');
        refetchDetail();
      }
    } finally {
      setSending(false);
    }
  };

  // AiMenuChatPage.tsx と同じ (2026-08-03、ユーザー報告): 生成途中で停止するボタン。
  // Designレビュー CRITICAL反映: 停止直後は justStopped で専用文言を出し、既存の
  // 「生成中」判定 (最後のメッセージがuser) と表示が矛盾しないようにする
  const [justStopped, setJustStopped] = useState(false);
  const stop = () => {
    abortRef.current?.abort();
    setStreamingText('');
    setJustStopped(true);
  };
  useEffect(() => {
    if (sessionDetail?.messages.at(-1)?.role === 'assistant') setJustStopped(false);
  }, [sessionDetail]);

  // 末尾が user のまま上限時間を超えたら「生成中」表示を打ち切る。ポーリング停止後も判定を
  // 進めるため、末尾が user の間だけ時計を進める
  const lastMessage = sessionDetail?.messages.at(-1);
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    if (lastMessage?.role !== 'user') return;
    const timer = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, [lastMessage?.role]);
  const awaitingGivenUp = !sending && isAwaitingGivenUp(lastMessage, nowTick);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4 h-[calc(100vh-140px)]">
      {/* 会話ペイン */}
      <div className="flex flex-col border border-zinc-100 rounded-xl bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-neutral-line flex items-center gap-2">
          <Link to="/ai/menu-profiles" className="p-1 -ml-1 text-neutral-sub hover:text-brand-red" aria-label="プロファイル一覧へ戻る"><ArrowLeft size={16} /></Link>
          <Sparkles size={18} className="text-brand-red" />
          <span className="font-h3 text-h3 truncate">プロファイル作成 (AI対話)</span>
        </div>

        {/* デザインレビュー反映 (2026-08-02): 登録内容プレビューはlg以上のみサイドパネル表示のため、
            lg未満では代替として折りたたみ式で同じ内容を表示する (AiMenuChatPage.tsxのモバイル対応と同様の方針) */}
        {draftProfile && (
          <details className="lg:hidden border-b border-neutral-line bg-zinc-50">
            <summary className="px-4 py-2 text-xs font-semibold text-zinc-500 cursor-pointer select-none">
              登録内容プレビュー: {draftProfile.profile_name} ({draftProfile.components.length}件)
            </summary>
            <div className="px-4 pb-3">
              {draftProfile.components.length === 0 ? (
                <div className="text-xs text-zinc-400">まだ構成要素は登録されていません</div>
              ) : (
                <ul className="space-y-1">
                  {draftProfile.components.map((comp) => (
                    <li key={comp.component_id} className="text-xs text-neutral-text">
                      <span className="text-zinc-400">{COMPONENT_TYPE_LABELS[comp.component_type] ?? comp.component_type}:</span> {comp.component_name}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </details>
        )}

        <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4">
          {(!sessionDetail || sessionDetail.messages.length === 0) && !streamingText && (
            <div className="text-sm text-zinc-400 text-center py-8">
              メッセージを送ると、AIが店舗のスープ等について質問しながら登録を進めます。
              まずは「はじめまして」など、気軽に送ってみてください。
            </div>
          )}
          {sessionDetail?.messages.map((m) => (
            <div key={m.message_id} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
              <div className={`max-w-[85%] rounded-xl px-4 py-2 text-sm whitespace-pre-wrap ${m.role === 'user' ? 'bg-brand-red text-white' : 'bg-zinc-50 text-neutral-text'}`}>
                {textOf(m.content) || (m.role === 'assistant' ? <span className="text-zinc-400 italic">(応答内容を取得できませんでした。もう一度送信してみてください)</span> : null)}
              </div>
            </div>
          ))}
          {streamingText && (
            <div className="flex justify-start">
              <div className="max-w-[85%] rounded-xl px-4 py-2 text-sm whitespace-pre-wrap bg-zinc-50 text-neutral-text" aria-live="polite">
                {streamingText}
              </div>
            </div>
          )}
          {sending && !streamingText && (
            <div className="flex justify-start"><Loader2 size={16} className="animate-spin text-zinc-400" /></div>
          )}
          {!sending && justStopped && (
            <div className="flex justify-start items-center gap-2 text-xs text-zinc-400">
              <Loader2 size={14} className="animate-spin" /> 停止処理中です…
            </div>
          )}
          {!sending && !justStopped && awaitingGivenUp && lastMessage && (
            <ResponseMissingNotice onResend={() => send(textOf(lastMessage.content))} />
          )}
          {!sending && !justStopped && !awaitingGivenUp && sessionDetail && sessionDetail.messages.length > 0 &&
            sessionDetail.messages.at(-1)?.role === 'user' && (
            <div className="flex justify-start items-center gap-2 text-xs text-zinc-400">
              <Loader2 size={14} className="animate-spin" /> AIが応答を生成中です (他の画面に移動しても生成は続きます)
            </div>
          )}
        </div>

        {finalized ? (
          <div className="p-3 border-t border-neutral-line bg-highlight-bg flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1 text-sm text-brand-black"><CheckCircle2 size={16} className="text-brand-gold" /> プロファイルが確定しました</span>
            {draftProfileId && (
              <button type="button" onClick={() => navigate(`/ai/menu-profiles/${draftProfileId}`)} className="px-3 py-1.5 rounded bg-brand-red text-white text-xs">
                登録内容を確認
              </button>
            )}
          </div>
        ) : (
          <div className="p-3 border-t border-neutral-line flex gap-2 items-end">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && e.shiftKey) { e.preventDefault(); send(input); } }}
              placeholder="メッセージを入力 (Enterで改行、Shift+Enterで送信)"
              rows={2}
              disabled={sending}
              className="flex-1 border border-neutral-line rounded px-3 py-2 text-sm resize-none disabled:opacity-50"
            />
            {sending ? (
              <button
                type="button"
                onClick={stop}
                className="p-2.5 rounded bg-neutral-text text-white hover:bg-black"
                aria-label="生成を停止"
                title="生成を停止"
              >
                <Square size={18} fill="currentColor" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => send(input)}
                disabled={!input.trim()}
                className="p-2.5 rounded bg-brand-red text-white disabled:opacity-50"
                aria-label="送信"
              >
                <Send size={18} />
              </button>
            )}
          </div>
        )}
      </div>

      {/* 登録内容プレビュー */}
      <div className="hidden lg:flex flex-col gap-2 border border-zinc-100 rounded-xl bg-white shadow-sm p-4 overflow-y-auto">
        <h2 className="text-sm font-semibold text-zinc-500">登録内容プレビュー</h2>
        {draftProfile ? (
          <div className="space-y-2">
            <div className="text-sm font-medium">{draftProfile.profile_name}</div>
            {draftProfile.components.length === 0 ? (
              <div className="text-xs text-zinc-400">まだ構成要素は登録されていません</div>
            ) : (
              <ul className="space-y-1">
                {draftProfile.components.map((comp) => (
                  <li key={comp.component_id} className="text-xs text-neutral-text">
                    <span className="text-zinc-400">{COMPONENT_TYPE_LABELS[comp.component_type] ?? comp.component_type}:</span> {comp.component_name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="text-xs text-zinc-400">対話を始めると内容が表示されます</div>
        )}
      </div>
    </div>
  );
}
